import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PeekListItem, PeekListItemButton, PeekListItemText, PeekStack, useTheme, } from "@piiqtechnologies/ui-components";
/** Icons sit at the size the footer items use, so every row in the sidebar reads alike. */
export const SIDEBAR_ICON_SIZE = 15;
/**
 * A page link in the sidebar. It answers "am I the current page?" from the route rather than
 * taking an isActive prop, so the three pages that use it — Home, Billing & Pricing, Account
 * Settings — cannot disagree with the URL or with each other.
 *
 * Given `onClick` instead, it is an action. Both shapes share this component so every row keeps
 * the same geometry — two row components with different insets is what made the labels fail to
 * line up.
 */
export const SidebarPageItem = ({ label, icon, to, onClick, badge, ariaLabel, expanded, }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isActive = Boolean(to) && pathname === to;
    const activeColor = theme.palette.teal[600];
    const opensDialog = expanded !== undefined;
    return (React.createElement(PeekListItem, { disablePadding: true },
        React.createElement(PeekListItemButton, { onClick: (event) => {
                onClick === null || onClick === void 0 ? void 0 : onClick(event);
                if (to)
                    navigate(to);
            }, "aria-current": isActive ? "page" : undefined, "aria-label": ariaLabel, "aria-haspopup": opensDialog ? "dialog" : undefined, "aria-expanded": opensDialog ? expanded : undefined, sx: {
                // Open (What's New's list beside it): grey, with the teal edge (Figma grey/bg, peek/teal).
                backgroundColor: isActive ? theme.palette.teal[100] : expanded ? "#f3f3f3" : "initial",
                // An inset shadow, not a border: a border takes part in the box model, so the active
                // row would sit 2px right of the inactive ones and the column would jitter on every
                // navigation.
                boxShadow: isActive
                    ? `inset 2px 0 0 ${activeColor}`
                    : expanded
                        ? `inset 2px 0 0 ${theme.palette.secondary.main}`
                        : "none",
                height: theme.spacing(4),
                // No negative margin: the row starts where the sidebar's padding puts it, which is
                // the same edge the dividers sit on.
                px: 1,
                color: isActive ? activeColor : "inherit",
            } },
            React.createElement(PeekStack, { direction: "row", alignItems: "center", spacing: 1, sx: { flex: 1, minWidth: 0 } },
                icon,
                React.createElement(PeekListItemText, { primary: label, primaryTypographyProps: {
                        variant: "body1",
                        color: isActive ? activeColor : undefined,
                        fontWeight: isActive ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
                    } })),
            badge)));
};
export default SidebarPageItem;
